import {
    BlurEvent,
    Button,
    ChangeEvent,
    ClickEvent,
    Component,
    DataDisplayEvent,
    DataSourceAction,
    DataSourceExecutionEvent,
    DataSourceMode,
    DropdownItem,
    Label,
    LookupModelSearchEvent,
    Snackbar,
    TableRow,
    TableRowDisplayEvent,
    Textbox,
    Toast,
    ValidationResult
} from "@mcleod/components";
import {
    Api,
    Color,
    CompanySettings,
    ConfigSettings,
    DateUtil,
    GeneralSettings,
    LicenseSettings,
    LogManager,
    Model,
    ModelRow,
    Navigation,
    StringUtil,
    UserSettings as CoreUserSettings
} from "@mcleod/core";
import {AutogenLayoutUserSettings} from "./autogen/AutogenLayoutUserSettings";
import {CommonDialogs} from "./CommonDialogs";
import {ValidationUtil} from "@mcleod/components/src/databinding/ValidationUtil";
import {ColorSelectorUtil} from "./ColorSelectorUtil";

const log = LogManager.getLogger("common.UserSettings");

export class UserSettings extends AutogenLayoutUserSettings {
    private _evenRowColor: Color;
    private _oddRowColor: Color;
    private _rateviewEnabled: boolean;
    private _hasDatOrgToken: boolean;
    private _hasDatRestService: boolean;
    private _datConnexionLoadboardService: boolean;
    private _displaySaveSuccessToastOnLoad: boolean;

    async onLoad() {
        if (this._displaySaveSuccessToastOnLoad) {
            Toast.showSuccessToast("You have successfully updated your User Settings!")
        }
        this.sourceCurrentUserAsset.preventFocusFirstField = true;
        this.sourceCurrentUserPowerbroker.preventFocusFirstField = true;
        this.sourceSettings.preventFocusFirstField = true;
        this.sourceUserRecognizedDevice.preventFocusFirstField = true;
        this.sourcePbwLoadboardServices.preventFocusFirstField = true;
        this.sourceEdiUser.preventFocusFirstField = true;
        this.sourceLmeUserToken.preventFocusFirstField = true;

        this.buttonSave.addClickListener((event: ClickEvent) => this.buttonSaveOnClick(event));
        this.setupTimeFormats();

        const partialShipmentEnabled = CompanySettings.get().partial_shipment_enabled;
        const partialShipLicense =  CompanySettings.get().partial_shipment_licensed;
        this.textboxPartialShipmentProfile.visible = partialShipmentEnabled;
        this.textboxPartialShipmentVendor.visible = partialShipmentEnabled && partialShipLicense;
        this.buttonSave.extraDataSources = [
            this.sourceUsers,
            this.sourceSettings,
            this.sourceCurrentUserPowerbroker,
            this.sourceCurrentUserAsset,
            this.sourceEdiUser,
            this.sourceLmeUserToken
        ];

        if (this.isEdiLicensed()) {
            this.sourceEdiUser.search({id: CoreUserSettings.getUserId()});
        }


        if (this.isRateIndexLicensed() || this.isPnnLicensed()) {
            const rateIndexResult = await this.sourceRateIndexControl.search({vendor: "T"})
            const datServiceResult = this.isPnnLicensed() ? await this.sourcePbwLoadboardServices.search({service_type: "T"}) : null;
            const datConnexionLoadboardService = this.isPnnLicensed() ?await this.sourcePbwLoadboardServices.search({service_type: "C"}) : null;

            const rateIndexDatServiceRow = rateIndexResult?.modelRows[0] as ModelRow;
            this._rateviewEnabled = rateIndexDatServiceRow?.get("dat_rateview_enabled");
            this._hasDatRestService = StringUtil.equalsIgnoreCase(datServiceResult?.modelRows[0]?.get("is_active"),"Y");
            this._datConnexionLoadboardService = StringUtil.equalsIgnoreCase(datConnexionLoadboardService?.modelRows[0]?.get("is_active"),"Y");

            if (this._rateviewEnabled || this._hasDatRestService) {
                this.panelDATFields.visible = this._datConnexionLoadboardService && !this._hasDatRestService;
                const orgTokenSearchResult = await this.sourceOrganizationToken.search({vendor: "T"});
                const rowOrgToken = orgTokenSearchResult?.modelRows[0] as ModelRow;
                this.panelRateViewDat.visible = true;
                this.imageDatLogo.visible = true;
                this._hasDatOrgToken = !StringUtil.isEmptyString(rowOrgToken?.get("organization_token"));
                this.textboxRateviewUserId.visible = this._hasDatOrgToken;
                this.buttonRateviewUserAuthorize.visible = this._hasDatOrgToken;

                if (this._hasDatOrgToken) {
                  await this.sourceLmeUserToken.search({user_id: CoreUserSettings.getUserId()});
                } else {
                    this.hideDatRestFields();
                }
            }
        }

        this.sourceSettings.data = [new ModelRow("common/user-system-config", false, {
            ...ConfigSettings.get(),
            user_id: CoreUserSettings.getUserId()
        })];
        this.sourceSettings.rowIndex = 0;
        this.addLayoutDataLoadListener(() => {
            if (window.history.state?.state?.scrollTo === this.tabBackOffice.id) {
                this.tabBackOffice.scrollIntoView();
            }
            window.history.replaceState({...window.history.state, scrollTo: null}, null);
        });

        await this.sourceUserRecognizedDevice.search({user_id: CoreUserSettings.getUserId()});

        this.textboxDATService.addBeforeLookupModelSearchListener((event) => {
            event.filter.service_type = 'C';
        });
        this.textboxTruckstopRESTService.addBeforeLookupModelSearchListener((event) => {
            event.filter.service_type = 'R';
        });
        this.textboxSegAllocCode.addBeforeLookupModelSearchListener((event) => {
            event.filter.alloc_type = 'M'
        })

        this.textboxConfTemplate.addBeforeLookupModelSearchListener((event) => {
            event.filter.document_type = 'Z';
            event.filter.template_number = ">0";
        });

        this.textboxQuoteTemplate.addBeforeLookupModelSearchListener((event) => {
            event.filter.document_type = 'Q';
            event.filter.template_number = ">0";
        });

        this.textboxLtlQuoteTemplate.addBeforeLookupModelSearchListener((event) => {
            event.filter.document_type = 'Y';
            event.filter.template_number = ">0";
        });
        this.setAllNewDevicesVisible();
        this.showHideCompanyTypeBasedComponents();
        if (!this.showFieldsBasedOnLicense()) {
            this.textboxSmsMessageLimit.visible = false;
            this.textboxMessageVisibility.visible = false;
        }
    }

    private setupRateViewFields() {
        this.panelRateViewSelection.visible = this._rateviewEnabled;
        this.serviceSpacer.visible = this._rateviewEnabled;
    }

    private setupDatRestFields(loadboardDatRestEnabled: boolean) {
        this.panelDatRestLoadboardSelection.visible = loadboardDatRestEnabled && !StringUtil.isEmptyString(this.textboxRateviewUserId.text);
    }

    private setupTimeFormats() {
        const civFormat = new DropdownItem("C", "Civilian");
        civFormat.setAddlValue("format", "hh:mma");
        const milFormat = new DropdownItem("M", "Military");
        milFormat.setAddlValue("format", "HHmm");
        this.textTimeFormat.items = [civFormat, milFormat];
    }

    setDataSourcesToPost() {
        const sources = [
            this.sourceUsers.post(),
            this.sourceSettings.post(),
            this.sourceCurrentUserPowerbroker.post(),
            this.sourceCurrentUserAsset.post()
        ];

        if (this.isEdiLicensed()) {
            sources.push(this.sourceEdiUser.post());
        }

        if(this._rateviewEnabled || this._hasDatRestService){
            sources.push(this.sourceLmeUserToken.post());
        }

        return sources;
    }


    private async setAllNewDevicesVisible() {
        const data = await Model.searchSingleRecord("common/user-settings-setup");
        if (data != null)
            this.checkboxAllowNewDevices.visible = data.getBoolean("user_new_device_override", false) === true;
    }

    async buttonSaveOnClick(event) {
        const validationResults: ValidationResult[] = this.validate(true, true);
        if (ValidationUtil.checkValidationResults(validationResults)) {
            this.buttonSave.busy = true;
            if (this.sourceCurrentUserPowerbroker.activeRow.get("loadboard_dat_id") == null) {
                this.sourceCurrentUserPowerbroker.activeRow.set("dat_user_id", null);
                this.sourceCurrentUserPowerbroker.activeRow.set("dat_password", null);
                this.textboxDatUserId.text = null;
                this.textboxDatPassword.text = null;
            }

            Promise.all(this.setDataSourcesToPost())
                .then(() => Navigation.reloadCurrentPage(true, { _displaySaveSuccessToastOnLoad: true }))
                .catch(error => CommonDialogs.showError(error))
                .finally(() => this.buttonSave.busy = false);
        } else {
            ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid))
        }
    }

    textDateFormatOnChange(event) {
        this.updateSample();
    }

    textTimeFormatOnChange(event) {
        this.updateSample();
    }

    updateSample() {
        const date = new Date();
        date.setFullYear(date.getFullYear(), 11, 31);
        date.setHours(16);
        date.setMinutes(35);
        let format: string;
        if (this.textDateFormat.selectedItem != null)
            format = this.textDateFormat.selectedItem.value;
        else
            format = "MM/dd/yyyy";
        format += " ";
        if (this.textTimeFormat.selectedItem != null)
            format += this.textTimeFormat.selectedItem.getAddlValue("format");
        else
            format += "hh:mma";
        this.labelSample.text = DateUtil.formatDateTime(date, format);
    }

    sourceCurrentUserPowerbrokerAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH && this.sourceCurrentUserPowerbroker.activeRow == null) {
            this.sourceCurrentUserPowerbroker.mode = DataSourceMode.ADD;
        }
        if (this.sourceUsers.activeRow) {
            const googleMapType = this.sourceUsers.activeRow.get("google_map_type");
            if (googleMapType !== undefined && "P" === googleMapType) {
                this.textboxGoogleMapDisplay.tooltip = "Standard+ uses a combination of Standard and Satellite Maps";
            }
        }
        if (this.sourceCurrentUserPowerbroker.activeRow) {
            const [datUser, datPass, datError, truckstopTokensExist] = [
                this.sourceCurrentUserPowerbroker.activeRow.get("dat_user_id"),
                this.sourceCurrentUserPowerbroker.activeRow.get("dat_password"),
                this.sourceCurrentUserPowerbroker.activeRow.get("dat_error"),
                this.sourceCurrentUserPowerbroker.activeRow.get("truckstop_tokens_exist")
            ]

            if (!StringUtil.isEmptyString(datUser) && !StringUtil.isEmptyString(datPass)) {
                this.setDatStatusLabel(datError, this.labelDATResult, false,this.buttonAuthorize);
            }

            if (truckstopTokensExist) {
                this.labelTruckstopResult.imageName = "circleCheck";
                this.labelTruckstopResult.imageColor = "success";
            } else {
                this.labelTruckstopResult.imageName = "circleX";
                this.labelTruckstopResult.imageColor = "error";
                this.labelTruckstopResult.tooltip = "Tokens not set for this user";
            }
        }
    }

    sourceLmeUserTokenAfterExecution(event: DataSourceExecutionEvent){
        if (DataSourceAction.SEARCH == event.getAction()) {
            if (this.sourceLmeUserToken.activeRow) {
                const [datUserId, datRateviewUserAuthError, isValidToken] = [
                    this.sourceLmeUserToken.activeRow.get("dat_user_id"),
                    this.sourceLmeUserToken.activeRow.get("user_auth_error"),
                    this.sourceLmeUserToken.activeRow.get("is_valid_token")
                ]
                if (this._rateviewEnabled || this._hasDatRestService) {
                    this.buttonRateviewUserAuthorize.visible = !StringUtil.isEmptyString(datRateviewUserAuthError);

                    if (!StringUtil.isEmptyString(datUserId)) {
                        this.setDatStatusLabel(datRateviewUserAuthError, this.labelRateViewDATResult, !isValidToken, this.buttonRateviewUserAuthorize);
                        this.panelServiceOptions.visible = isValidToken;
                        this.setupRateViewFields();
                        this.setupDatRestFields(this._hasDatRestService && isValidToken);
                    }
                } else {
                    this.hideDatRestFields()
                }
            } else {
                this.panelServiceOptions.visible = false;
                //check if we have a dat user id in pnn transcore table
                this.sourcePnnTranscoreUser.search({user_id: CoreUserSettings.getUserId()}).then((response) => {
                    const datUserId = response?.modelRows[0]?.get("transcore_user_id");
                    if (!StringUtil.isEmptyString(datUserId)) {
                        this.textboxRateviewUserId.text = datUserId;
                    }
                });
            }
        }
    }

    textboxRateviewUserIdOnChange(event: ChangeEvent) {
        this.buttonRateviewUserAuthorize.visible = !StringUtil.isEmptyString(event.newValue);
        this.panelServiceOptions.visible = false;
        this.labelRateViewDATResult.visible = false;
        if (!this.buttonRateviewUserAuthorize.visible) {
            this.buttonRateviewUserAuthorize.tooltip = "Please provide a DAT User ID";
        } else {
            this.buttonRateviewUserAuthorize.tooltip = null;
        }
    }

    sourceCurrentUserAssetAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH && this.sourceCurrentUserAsset.activeRow == null) {
            this.sourceCurrentUserAsset.mode = DataSourceMode.ADD;
        }
    }

    /** This is an event handler for the onClick event of buttonToggleDeviceApproval.  */
    buttonToggleDeviceApprovalOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        tableRow.data.set("approved", tableRow.data.get("approved") === "N" ? "Y" : "N");
        tableRow.data.post().then(() => this.tableUserDevices.redisplaySingleRow(tableRow.index, tableRow.data, false, false));
    }

    /** This is an event handler for the onClick event of buttonAuthorize.  */
    buttonAuthorizeOnClick(event: ClickEvent) {
        this.labelTruckstopResult.imageColor = "primary";
        this.labelTruckstopResult.busy = true;
        const [serviceId, userId, password] = [
            this.textboxTruckstopRESTService.getDataValue(),
            this.textboxTruckstopUserId.text,
            this.textboxTruckstopPassword.text
        ];

        if (StringUtil.isEmptyString(serviceId)
            || StringUtil.isEmptyString(userId)
            || StringUtil.isEmptyString(password)) {
            this.labelTruckstopResult.imageName = null;
            this.labelTruckstopResult.imageColor = "primary";
            this.labelTruckstopResult.busy = false;
            this.labelTruckstopResult.tooltip = null;
            Snackbar.showWarningSnackbar("Please provide all Truckstop REST fields (Service, Truckstop User ID, Password");
        } else {
            Api.post("lme/powerbroker/truckstop-user-auth", {
                "service_id": serviceId,
                "user_id": userId,
                "password": password
            }).then(response => {
                this.labelTruckstopResult.busy = false;
                if (response.data[0].success) {
                    this.sourceCurrentUserPowerbroker.activeRow.set("truckstop_access_token", response.data[0].access);
                    this.sourceCurrentUserPowerbroker.activeRow.set("truckstop_refresh_token", response.data[0].refresh);
                    this.sourceCurrentUserPowerbroker.activeRow.set("truckstop_token_expires", response.data[0].expire);

                    this.labelTruckstopResult.imageName = "circleCheck";
                    this.labelTruckstopResult.imageColor = "success";
                    this.textboxTruckstopPassword.clear();
                    this.labelTruckstopResult.tooltip = null;
                    Snackbar.showSnackbar("Success: Access tokens saved, Truckstop password not saved");
                } else {
                    this.labelTruckstopResult.imageName = "circleX";
                    this.labelTruckstopResult.imageColor = "error";
                    this.labelTruckstopResult.tooltip = response.data[0].error ? response.data[0].error : "There was an unexpected error Authorizing access.";
                }
            }).catch(reason => this.labelTruckstopResult.busy = false);
        }
    }

    buttonDatAuthorizeOnClick(event: ClickEvent) {
        this.labelRateViewDATResult.visible = true;
        this.labelRateViewDATResult.imageColor = "primary";
        this.labelRateViewDATResult.busy = true;
        if (StringUtil.isEmptyString(this.textboxRateviewUserId.text)) {
            this.labelRateViewDATResult.imageName = null;
            this.labelRateViewDATResult.imageColor = "primary";
            this.labelRateViewDATResult.busy = false;
            this.labelRateViewDATResult.tooltip = null;
            Snackbar.showWarningSnackbar("Please provide DAT User ID");
        } else {
            Api.post("lme/general/rateindex/rateview/auth", {
                "action_type": this._rateviewEnabled ? "U" : this._hasDatRestService ? "UL" : "",
                "user_id": CoreUserSettings.getUserId(),
                "dat_rateview_user": this.textboxRateviewUserId.text
            }).then(async response => {
                if(StringUtil.equalsIgnoreCase(response.data[0].dat_user_auth_error, "Authentication is blocked for one minute.")) {
                    Snackbar.showWarningSnackbar("Authentication is blocked for one minute.");
                }
                this.labelRateViewDATResult.busy = false;
                if (response?.data[0]?.is_valid_token) {
                    this.labelRateViewDATResult.tooltip = "User is successfully authenticated.";
                    this.labelRateViewDATResult.imageName = "circleCheck";
                    this.labelRateViewDATResult.imageColor = "success";
                    this.buttonRateviewUserAuthorize.visible = false;
                    this.switchEnabledRateView.checked = this._rateviewEnabled;
                    this.switchLoadBoard.checked = this._hasDatRestService;
                    this.panelServiceOptions.visible = true;
                } else {
                    this.labelRateViewDATResult.imageName = "circleX";
                    this.labelRateViewDATResult.imageColor = "error";
                    this.labelRateViewDATResult.tooltip = response?.data[0]?.dat_user_auth_error;
                    this.panelServiceOptions.visible = false;
                }
                await this.sourceLmeUserToken.search({user_id: CoreUserSettings.getUserId()});

            }).catch(reason => this.labelRateViewDATResult.busy = false);
        }
    }

    /** This is an event handler for the onRowDisplay event of tableUserDevices.  */
    tableUserDevicesOnRowDisplay(event: TableRowDisplayEvent) {
        const button = event.getTableRow().findComponentById("buttonToggleDeviceApproval") as Button;
        if (button == null) {
            return;
        }
        
        if (event.getTableRow().data.get("approved") === "N")
            button.setProps({ caption: "Unblock this Device", color: "success" });
        else
            button.setProps({ caption: "Block this Device", color: "warning" });
    }

    /** This is an event handler for the onDataDisplay event of labelDeviceApproved.  */
    labelDeviceApprovedOnDataDisplay(event: DataDisplayEvent) {
        const label = event.target as Label;
        const tableRow = TableRow.getContainingTableRow(label);
        const thisDeviceId = localStorage.getItem("deviceId");
        if (tableRow.data.get("approved") === "N")
            label.setProps({ text: "Blocked", backgroundColor: "warning", color: "warning.reverse" });
        else if (tableRow.data.get("device_id") === thisDeviceId)
            label.setProps({ text: "This Device", backgroundColor: "success", color: "success.reverse" });
        else
            label.setProps({ text: "Approved", backgroundColor: "success", color: "success.reverse" });
    }

    /** This is an event handler for the onDataDisplay event of label1.  */
    labelDeviceLocationOnDataDisplay(event: DataDisplayEvent) {
        const label = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(label);
        if (StringUtil.isEmptyString(tableRow.data.get("last_ip_location")))
            label.text = "Unknown location";
        label.tooltip = "IP address: " + tableRow.data.get("last_ip_address");
    }

    showHideCompanyTypeBasedComponents() {
        const companySettings = CompanySettings.get();
        const isAsset = companySettings?.is_asset;
        const isBrokerage = companySettings?.is_brokerage;
        const isBoth = isAsset && isBrokerage;
        const hasEarlyAdoptersAccess = GeneralSettings.hasEarlyAdoptersAccess();

        this.textboxDriverManagerProfile.visible = isAsset;
        this.textboxOrderPlanningProfile.visible = isAsset;
        this.textboxAssetOrderNextAction.visible = isAsset;

        this.panelBpProfiles.visible = isBrokerage;
        this.textboxBpProfile.required = isBrokerage && !(isAsset && isBrokerage);
        this.textboxTmProfile.visible = isBrokerage;
        this.textboxDefaultDistanceMarginPercentage.visible = isBrokerage;
        this.textboxBrokerageOrderNextAction.visible = isBrokerage;
        this.tabIntegrationServices.visible = isBrokerage;

        if (isBoth) {
            this.textboxDefMoveType.visible = true;
            this.textboxBrokerageOrderNextAction.caption = "PowerBroker New Order Next Action";
            this.textboxAssetOrderNextAction.caption = "Loadmaster New Order Next Action";
        }
    }

    showFieldsBasedOnLicense() {
        return !!LicenseSettings.getSingleton()?.getLicensedPackages()?.find(license => license === "com.tms.client.loadmaster.sms");
    }

    sourceUsersAfterModeChange(event) {
        if ((event.newMode == DataSourceMode.SEARCH || event.newMode == DataSourceMode.NONE) && event.newMode != event.oldMode) {
            const allocationEnabled = this.sourceUsers.activeRow?.get("seg_alloc_enabled");
            this.textboxSegAllocCode.visible = allocationEnabled;
            delete this.sourceUsers.activeRow?.data["seg_alloc_enabled"];
        }
    }

    /** This is an event handler for the onClick event of buttonEvenColor. */
    async buttonEvenColorOnClick() {
        const selectedColor = await ColorSelectorUtil.promptForColor();
        log.debug("Selected even row color: %o", selectedColor);
        this.evenRowColor = selectedColor;
    }

    /** This is an event handler for the onClick event of buttonOddColor. */
    async buttonOddColorOnClick() {
        const selectedColor = await ColorSelectorUtil.promptForColor();
        log.debug("Selected odd row color: %o", selectedColor);
        this.oddRowColor = selectedColor;
    }

    /** This is an event handler for the onClick event of buttonClearEvenColor. */
    buttonClearEvenColorOnClick() {
        this.evenRowColor = null;
    }

    /** This is an event handler for the onClick event of buttonClearOddColor. */
    buttonClearOddColorOnClick() {
        this.oddRowColor = null;
    }

    private get evenRowColor(): Color {
        return this._evenRowColor;
    }

    private set evenRowColor(value: Color) {
        if (value === this._evenRowColor)
            return;
        this._evenRowColor = value;
        this.sourceUsers.activeRow.set("table_even_row_color", this.evenRowColor);
        //don't set color when in the designer...we don't want to serialize that color as part of the layout
        if (this._designer == null)
            this.panelEvenColorDisplay.backgroundColor = this._evenRowColor ?? "";
    }

    private get oddRowColor(): Color {
        return this._oddRowColor;
    }

    private set oddRowColor(value: Color) {
        if (value === this._oddRowColor)
            return;
        this._oddRowColor = value;
        this.sourceUsers.activeRow.set("table_odd_row_color", this.oddRowColor);
        //don't set color when in the designer...we don't want to serialize that color as part of the layout
        if (this._designer == null)
            this.panelOddColorDisplay.backgroundColor = this._oddRowColor ?? "";
    }

    /** This is an event handler for the afterExecution event of sourceUsers. */
    sourceUsersAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.evenRowColor = this.sourceUsers.activeRow.get("table_even_row_color", undefined);
            this.oddRowColor = this.sourceUsers.activeRow.get("table_odd_row_color", undefined);
        }
        else if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE)
            CoreUserSettings.getSingleton().updateEvenOddRowColors(this.evenRowColor, this.oddRowColor);
    }

    textboxTmProfileBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }
    //If the datasource returns a null row then change to ADD mode
    sourceEdiUserAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH && this.sourceEdiUser.activeRow == null) {
            this.sourceEdiUser.mode = DataSourceMode.ADD;
        }
    }

    isEdiLicensed() {
        return !!LicenseSettings.getSingleton()?.getLicensedPackages()?.find(license => license === "com.tms.client.loadmaster.edi");
    }

    isPnnLicensed() {
        return !!LicenseSettings.getSingleton()?.getLicensedPackages()?.find(license => license === "com.tms.client.loadmaster.pnn");
    }

    private isRateIndexLicensed() {
        const licensedPackages = LicenseSettings.getSingleton()?.getLicensedPackages() || [];
        return licensedPackages.includes("com.tms.client.loadmaster.rateindex.EntryLicenseCombo");
    }


    private setDatStatusLabel(datError: string, label: Label, showUserAuthButton: boolean, buttonAuthorize: Button) {
        label.visible = true;
        buttonAuthorize.visible = showUserAuthButton;
        if (StringUtil.isEmptyString(datError) && !showUserAuthButton) {
            label.imageName = "circleCheck";
            label.imageColor = "success";
            label.tooltip = "User is successfully authenticated.";
        } else {
            label.imageName = "circleX";
            label.imageColor = "error";
            label.tooltip = datError;
        }
    }

    private hideDatRestFields() {
        this.buttonRateviewUserAuthorize.visible = false;
        this.textboxRateviewUserId.visible = false;
        this.labelRateViewDATResult.visible = false;
        this.panelRateViewDat.visible = false;
    }

    textboxTruckstopRESTServiceOnChange(event: ChangeEvent) {
        this.buttonAuthorize.visible = !StringUtil.isEmptyString(event.newValue) && !StringUtil.isEmptyString(this.textboxTruckstopUserId.text) && !StringUtil.isEmptyString(this.textboxTruckstopPassword.text);
    }

    textboxTruckstopUserIdOnChange(event: ChangeEvent) {
        this.buttonAuthorize.visible = !StringUtil.isEmptyString(event.newValue) && !StringUtil.isEmptyString(this.textboxTruckstopRESTService.text) && !StringUtil.isEmptyString(this.textboxTruckstopPassword.text);
    }

    textboxTruckstopPasswordOnChange(event: ChangeEvent) {
        this.buttonAuthorize.visible = !StringUtil.isEmptyString(event.newValue) && !StringUtil.isEmptyString(this.textboxTruckstopRESTService.text) && !StringUtil.isEmptyString(this.textboxTruckstopUserId.text);
    }

    textboxRateviewUserIdOnBlur(event: BlurEvent){
        const textboxDatUserId = event.target as Textbox;
        if(StringUtil.isEmptyString(textboxDatUserId.text)){
            this.panelDatRestLoadboardSelection.visible = false;
            this.switchEnabledRateView.checked = false;
            this.switchLoadBoard.checked = false;
        }
    }
}
